import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
} from '@angular/forms';
import {
    KAL_DIALOG_DATA,
    KalDialogConfig,
    KalDialogRef
} from '@kalidea/kaligraphi';
import {
    of
} from 'rxjs';
import {
    tap
} from 'rxjs/operators';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    SharedAdvancedRuleComponent
} from 'src/app/_shared/components/shared-advanced-rule/shared-advanced-rule.component';
import {
    AdvancedRule,
    AdvancedRuleService
} from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import { TypeActivite } from 'src/app/board/activity/activity.constants';
import {
    RegleAvanceeModelDto,
    TypesRegleEnum
} from 'src/graphql/generated-types';
import { constants } from 'src/environments/constants';

export type RuleTypeAndValue = [ruleType: string, valueType: string];
export type ComplementarySubventionInfo = {
    IsSubventionEnveloppeComplementaire: boolean
    IdEnveloppeComplementaire: number,
    Ordre: number
};

export interface SharedAdvancedRuleEditDialogData {

    ruleId?: number;

    ruleType?: TypesRegleEnum;

    isInterActivity?: boolean;

    saveAfterSubmit?: boolean;

    activityType?: TypeActivite;

    accepted?: boolean;

    isDuplicate?: boolean;

    ruleLabel?: string;

    changeToSimpleMode?: boolean;

    complementarySubvention?: ComplementarySubventionInfo

    rule?: RegleAvanceeModelDto;

    isPlanning?: boolean;

    shouldHideSwitchButton?: boolean;

}

@Component({
    selector: 'app-shared-advanced-rule-edit-dialog',
    templateUrl: './shared-advanced-rule-edit-dialog.component.html',
    styleUrls: ['./shared-advanced-rule-edit-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAdvancedRuleEditDialogComponent extends BaseComponent implements OnInit {

    static readonly ruleTypeToCatalogRuleTypeAndValue: Record<TypesRegleEnum, RuleTypeAndValue> = {
        [TypesRegleEnum.Accessibilite]: [constants.rulesType.acc, null],
        [TypesRegleEnum.Subvention]: [constants.rulesType.subv, null],
        [TypesRegleEnum.Enveloppe]: [constants.rulesType.lim, 'M'],
        [TypesRegleEnum.QuotaFaciale]: [constants.rulesType.lim, 'F'],
        [TypesRegleEnum.QuotaNombre]: [constants.rulesType.lim, 'N'],
        [TypesRegleEnum.RestantACharge]: [constants.rulesType.rac, null],

        // en théorie non utilisés
        [TypesRegleEnum.None]: [null, null],
        [TypesRegleEnum.Quota]: [constants.rulesType.lim, 'F'],
    };

    static readonly rulesTypesLabel: Record<(TypesRegleEnum & { Abondement }) , string> = {
        Abondement: 'd\'abondement',
        [TypesRegleEnum.Accessibilite]: 'd\'accessibilité',
        [TypesRegleEnum.Subvention]: 'de subvention',
        [TypesRegleEnum.Enveloppe]: 'd\'enveloppe',
        [TypesRegleEnum.Quota]: 'de quota',
        [TypesRegleEnum.QuotaFaciale]: 'de quota',
        [TypesRegleEnum.QuotaNombre]: 'de quota',
        [TypesRegleEnum.None]: '',
        [TypesRegleEnum.RestantACharge]: 'de restant à charge',
    };

    formControl: FormControl<AdvancedRule | RegleAvanceeModelDto | null> = new FormControl<AdvancedRule | RegleAvanceeModelDto | null>(null);

    @ViewChild(SharedAdvancedRuleComponent) advancedRuleComponent: SharedAdvancedRuleComponent;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder,
                private readonly dialogRef: KalDialogRef<SharedAdvancedRuleEditDialogComponent, SharedAdvancedRuleEditDialogData>,
                private readonly advancedRuleService: AdvancedRuleService,
                @Inject(KAL_DIALOG_DATA) public data: SharedAdvancedRuleEditDialogData) {
        super(cdr);
    }

    get hasId(): boolean {
        return !!this.formControl.value?.Id;
    }

    get shouldShowSwitchButton(): boolean {
        const  shouldHideSwitchButton =  typeof this.data.shouldHideSwitchButton === 'boolean'? this.data.shouldHideSwitchButton : false;
        return !(!!shouldHideSwitchButton || (!shouldHideSwitchButton && !!this.hasId));
    }

    get activityType(): TypeActivite {
        return this.data?.activityType;
    }

    static defaultDialogConfig(data: SharedAdvancedRuleEditDialogData): KalDialogConfig<SharedAdvancedRuleEditDialogData> {
        const {
            ruleId,
            ruleType,
            ruleLabel,
            activityType
        } = data;
        const ruleTypeForLabel = activityType === TypeActivite.Epargnes && ruleType === TypesRegleEnum.Subvention ? 'Abondement' : ruleType;
        let title = `${!ruleId ? 'Création' : 'Modification'} d'une règle ${SharedAdvancedRuleEditDialogComponent.rulesTypesLabel[ruleTypeForLabel]}`;
        if (!!ruleLabel) {
            title = `Détail de la règle ${SharedAdvancedRuleEditDialogComponent.rulesTypesLabel[ruleTypeForLabel]}`;
        }

        return new KalDialogConfig<SharedAdvancedRuleEditDialogData>({
            hasBackdrop: true,
            width: '90vw',
            height: '100vh',
            backdropClass: 'cdk-overlay-dark-backdrop',
            title,
            data
        });
    }

    static afpaRuleTypeToTypesRegleEnum(ruleType: string, valueType: string): TypesRegleEnum {
        if (!ruleType) {
            return TypesRegleEnum.None;
        }

        if (ruleType === constants.rulesType.acc) {
            return TypesRegleEnum.Accessibilite;
        }

        if (ruleType === constants.rulesType.subv) {
            if (valueType === constants.rulesType.rac) {
                return TypesRegleEnum.RestantACharge;
            }
            return TypesRegleEnum.Subvention;
        }

        // ruleType === 'LIM' en théorie
        if (valueType === 'M') {
            return TypesRegleEnum.Enveloppe;
        }

        return TypesRegleEnum.Quota;
    }

    close(): void {
        this.dialogRef.close({
            accepted: false,
            changeToSimpleMode: false
        });
    }

    closeToSimpleMode(): void {
        this.dialogRef.close({
            accepted: false,
            changeToSimpleMode: true
        });
    }

    save(): void {
        const formValues = {
            ...this.formControl.getRawValue(),
            ...this.advancedRuleComponent.form.getRawValue()
        };

        const {
            IsSubventionEnveloppeComplementaire,
            IdEnveloppeComplementaire,
            Ordre
        } = formValues;

        const rule = this.advancedRuleService.normalize(formValues);

        const action$ = !!this.data.saveAfterSubmit
            ? this.advancedRuleService.saveAdvancedRule$({Regle: rule}) // Si on est en type interactivité on enregistre directement.
            : of(rule.Id);

        this.watch$ = this.wrapLoader$(action$, 'save').pipe(
            tap(ruleId => {
                rule.Id = ruleId || 0;
                this.dialogRef.close({
                    ruleId,
                    rule,
                    complementarySubvention: {
                        IsSubventionEnveloppeComplementaire,
                        IdEnveloppeComplementaire,
                        Ordre: Ordre || 1
                    },
                    accepted: true
                });
            })
        );
    }

    ngOnInit(): void {
        if (!!this.data.rule) {
            this.formControl.setValue(this.data.rule);
        }   
    }
}
